// Month Calculator logic
import {
  differenceInCalendarMonths,
  differenceInCalendarDays,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  getDaysInMonth,
  format,
  addDays,
} from "date-fns";
import { gregorianToBangla } from "@/lib/bengaliDate";
import { gregorianToHijri } from "@/lib/calculators/dateCalculator";

export interface MonthsBetween {
  months: number;
  days: number;
  totalMonthsDecimal: number;
}

export function monthsBetween(start: Date, end: Date): MonthsBetween {
  const a = start <= end ? start : end;
  const b = start <= end ? end : start;
  let months = differenceInCalendarMonths(b, a);
  let after = addMonths(a, months);
  if (after > b) {
    months -= 1;
    after = addMonths(a, months);
  }
  const days = differenceInCalendarDays(b, after);
  const monthLen = getDaysInMonth(after);
  const totalMonthsDecimal = months + days / monthLen;
  return { months, days, totalMonthsDecimal };
}

export function shiftMonths(start: Date, n: number, op: "add" | "sub"): {
  result: Date;
  clamped: boolean;
} {
  const fn = op === "add" ? addMonths : subMonths;
  const result = fn(start, n);
  const clamped = result.getDate() !== start.getDate();
  return { result, clamped };
}

export interface MonthInRange {
  date: Date; // first of month
  label: string; // "January 2026"
  daysInMonth: number;
  banglaSpan: { early: { year: number; monthBn: string; monthEn: string }; late: { year: number; monthBn: string; monthEn: string } };
  hijriSpan: { early: { year: number; monthBn: string; monthEn: string }; late: { year: number; monthBn: string; monthEn: string } };
}

export function monthsInRange(start: Date, end: Date): MonthInRange[] {
  const a = start <= end ? start : end;
  const b = start <= end ? end : start;
  const out: MonthInRange[] = [];
  let cur = startOfMonth(a);
  const stop = startOfMonth(b);
  // Safety cap
  let safety = 0;
  while (cur <= stop && safety++ < 600) {
    const last = endOfMonth(cur);
    const banglaEarly = gregorianToBangla(cur);
    const banglaLate = gregorianToBangla(last);
    const hijriEarly = gregorianToHijri(cur);
    const hijriLate = gregorianToHijri(last);
    out.push({
      date: cur,
      label: format(cur, "MMMM yyyy"),
      daysInMonth: getDaysInMonth(cur),
      banglaSpan: {
        early: { year: banglaEarly.year, monthBn: banglaEarly.monthBn, monthEn: banglaEarly.monthEn },
        late: { year: banglaLate.year, monthBn: banglaLate.monthBn, monthEn: banglaLate.monthEn },
      },
      hijriSpan: {
        early: { year: hijriEarly.year, monthBn: hijriEarly.monthBn, monthEn: hijriEarly.monthEn },
        late: { year: hijriLate.year, monthBn: hijriLate.monthBn, monthEn: hijriLate.monthEn },
      },
    });
    cur = addMonths(cur, 1);
  }
  return out;
}

export { addDays };
